import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { useUpdateConnectionSettings } from '@utils/api/connections/use-youtube-publish-settings.tsx';
import type { Connection } from '@utils/types/connections/connection.ts';

interface Props {
  connection: Connection;
}

/**
 * Per-connection toggle for the YouTube new-video upload path.
 *
 * Lives in each YouTube row of the Connections list — one org can run
 * a captions-only connection next to an upload-capable one. Captions
 * publish keeps working either way; the flag only gates the
 * ``videos.insert`` path on the backend worker + API guard.
 */
export default function YouTubeUploadToggle({ connection }: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { mutate: update, isLoading: isSaving } = useUpdateConnectionSettings(
    connection.id
  );

  const enabled = !!connection.video_uploads_enabled;

  const toggle = () => {
    update(
      { video_uploads_enabled: !enabled },
      {
        onSuccess: () => {
          toast.success(t('publishing-connection-upload-saved'));
        },
        onError: () => {
          toast.error(t('publishing-connection-upload-save-failed'));
        }
      }
    );
  };

  return (
    <label
      className="flex cursor-pointer items-center gap-2 text-xs"
      style={{ color: getColour('inactiveText') }}
    >
      <span className="whitespace-nowrap">
        {t('publishing-connection-upload-label')}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        onClick={toggle}
        disabled={isSaving}
        aria-label={t('publishing-connection-upload-aria', {
          account: connection.account_name
        })}
        className="relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full outline-none focus-visible:ring-2 disabled:opacity-50"
        style={{
          background: enabled ? getColour('activeText') : getColour('hairline')
        }}
      >
        <span
          className="inline-block h-4 w-4 rounded-full bg-white shadow transition-transform"
          style={{
            transform: enabled ? 'translateX(18px)' : 'translateX(2px)'
          }}
        />
      </button>
    </label>
  );
}
